import React, { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { ReactComponent as SearchIcon } from "../images/svg/search.svg";
import { colour } from "../data/colour";

import foodBg from "../images/food_bg.png";
import drinkBg from "../images/drink_bg.png";

const MenuBar = ({ menuData }) => {
  const [activeMainCategory, setActiveMainCategory] = useState(null);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});

  // Memoize the main categories calculation
  const mainCategories = useMemo(() => {
    return menuData
      ? [...new Set(menuData.map((item) => item.category))].sort((a, b) =>
          a.localeCompare(b)
        )
      : [];
  }, [menuData]);

  // Set the first main category as active when menuData changes
  useEffect(() => {
    if (mainCategories.length > 0 && !activeMainCategory) {
      setActiveMainCategory(mainCategories[0]);
    }
  }, [menuData, mainCategories, activeMainCategory]);

  // Extract and sort subcategories for the active main category alphabetically
  const getSubCategories = (mainCat) => {
    if (!menuData) return [];
    return [
      ...new Set(
        menuData
          .filter((item) => item.category === mainCat)
          .map((item) => item.type)
      ),
    ].sort((a, b) => a.localeCompare(b));
  };

  // Get items for a specific subcategory (already filtered by mainCat & subCat)
  const getItemsForSubCategory = (mainCat, subCat) => {
    if (!menuData) return [];
    return menuData.filter(
      (item) => item.category === mainCat && item.type === subCat
    );
  };

  const toggleMainCategory = (category) => {
    setActiveMainCategory(category);
    setActiveSubCategory(null);
    setSearchTerms({});
  };

  const toggleSubCategory = (category) => {
    setActiveSubCategory(activeSubCategory === category ? null : category);
  };

  const handleSearchChange = (subCategory, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [subCategory]: value,
    }));
  };

  // Filter options based on search term for specific subcategory
  const filteredOptions = (mainCat, subCategory) => {
    const searchTerm = searchTerms[subCategory] || "";
    const items = getItemsForSubCategory(mainCat, subCategory);

    if (!searchTerm) return items;

    return items.filter((item) =>
      item.product.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1000px",
        zIndex: 1000,
        margin: "auto",
      }}
    >
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          color: colour.accent,
          fontSize: "2rem",
        }}
      >
        MENU
      </Box>

      {/* Main Category Bar */}
      <Box
        sx={{
          width: "90%",
          margin: "auto",
          position: "relative",
          zIndex: 1001,
        }}
      >
        <Box
          sx={{
            display: "flex",
            backgroundColor: "transparent",
          }}
        >
          {mainCategories.map((category) => {
            const bgImage =
              category.toLowerCase() === "food" ? foodBg : drinkBg;

            return (
              <Box
                key={category}
                sx={{
                  width: `${100 / mainCategories.length}%`,
                  textAlign: "center",
                  padding: "1.25rem 0",
                  cursor: "pointer",
                  textTransform: "capitalize",
                  borderRadius: "0.5rem 0.5rem 0 0",
                  position: "relative",
                  zIndex: 1000,
                  ...(activeMainCategory === category && {
                    backgroundColor: colour.accent,
                    color: "white",
                    fontWeight: "bold",
                  }),
                  ...(activeMainCategory !== category && {
                    color: "white",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${bgImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "grayscale(100%) blur(1px)",
                      borderRadius: "0.5rem 0.5rem 0 0",
                      zIndex: -1,
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      borderRadius: "0.5rem 0.5rem 0 0",
                      zIndex: -1,
                    },
                  }),
                  "&:hover": {
                    color: "black",
                  },
                  "&::after": activeMainCategory === category && {
                    content: '""',
                    position: "absolute",
                    bottom: "-10px",
                    left: 0,
                    right: 0,
                    height: "10px",
                    backgroundColor: colour.accent,
                    zIndex: 1001,
                  },
                }}
                onClick={() => toggleMainCategory(category)}
              >
                <Box sx={{ position: "relative", zIndex: 1 }}>{category}</Box>
              </Box>
            );
          })}
        </Box>

        {/* Dropdown Container */}
        {activeMainCategory && (
          <Box
            sx={{
              position: "relative",
              backgroundColor: colour.accent,
              padding: "1.5rem 0",
              borderRadius:
                activeMainCategory === mainCategories[0]
                  ? "0 0.5rem 0.5rem 0.5rem"
                  : "0.5rem 0 0.5rem 0.5rem",
              width: "100%",
              boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
              zIndex: 1000,
              top: "-10px",
            }}
          >
            {/* Subcategories (now sorted A-Z) */}
            {getSubCategories(activeMainCategory).map((subCategory) => (
              <Box key={subCategory} sx={{ marginBottom: "0.25rem" }}>
                <Box
                  sx={{
                    padding: "0.5rem 0.75rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor:
                      activeSubCategory === subCategory
                        ? colour.light
                        : "transparent",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: colour.light,
                    },
                  }}
                  onClick={() => toggleSubCategory(subCategory)}
                >
                  <Box
                    sx={{
                      marginRight: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      fontSize:
                        activeSubCategory === subCategory ? "1.5rem" : "1rem",
                      lineHeight: 1,
                    }}
                  >
                    {subCategory === "beer"
                      ? "🍺"
                      : subCategory === "energy drinks"
                      ? "⚡"
                      : subCategory === "soft drinks"
                      ? "🥤"
                      : subCategory === "cocktails"
                      ? "🍹"
                      : subCategory === "vodka"
                      ? "🍸"
                      : subCategory === "juices"
                      ? "🥤"
                      : subCategory === "youghurt"
                      ? "🥛"
                      : subCategory === "rum"
                      ? "🥃"
                      : subCategory === "gin"
                      ? "🥃"
                      : subCategory === "tequila"
                      ? "🍹"
                      : subCategory === "champagne"
                      ? "🍾"
                      : subCategory === "red wine"
                      ? "🍷"
                      : subCategory === "non alcoholic wine"
                      ? "🍷"
                      : subCategory === "sparkling wine"
                      ? "🍷"
                      : subCategory === "cream liquor"
                      ? "🥛"
                      : subCategory === "spirits"
                      ? "🥃"
                      : subCategory === "aperitif"
                      ? "🍸"
                      : subCategory === "whiskey"
                      ? "🥃"
                      : subCategory === "cognac"
                      ? "🥃"
                      : subCategory === "water"
                      ? "💧"
                      : subCategory === "peppersoups"
                      ? "🍲"
                      : subCategory === "meals"
                      ? "🍴"
                      : subCategory === "peppered meat"
                      ? "🍖"
                      : subCategory === "shawarma"
                      ? "🌯"
                      : subCategory === "grill"
                      ? "🔥"
                      : subCategory === "amala hut"
                      ? "🍜"
                      : subCategory === "takeaway pack"
                      ? "📦"
                      : "🍽️"}
                  </Box>
                  <Box
                    sx={{
                      textTransform: "capitalize",
                      fontWeight:
                        activeSubCategory === subCategory ? "bold" : "normal",
                      fontSize:
                        activeSubCategory === subCategory ? "1.5rem" : "1rem",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {subCategory}
                  </Box>
                </Box>

                {/* Options for the subcategory */}
                {activeSubCategory === subCategory && (
                  <Box
                    sx={{
                      marginTop: "0.5rem",
                      padding: "1rem 0",
                      backgroundColor: "rgba(255,255,255,0.2)",
                    }}
                  >
                    {/* Search Box for this subcategory */}
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        margin: "0.5rem 1rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        boxShadow:
                          "inset 0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "0.5rem",
                        backgroundColor: "white",
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <input
                          type="text"
                          value={searchTerms[subCategory] || ""}
                          onChange={(e) =>
                            handleSearchChange(subCategory, e.target.value)
                          }
                          placeholder={`Search ${subCategory}...`}
                          style={{
                            width: "90%",
                            padding: "0.25rem",
                            background: "none",
                            border: "none",
                            outline: "none",
                            color: colour.primary,
                          }}
                        />
                      </Box>
                      <Box sx={{ display: "grid", placeItems: "center" }}>
                        <SearchIcon
                          style={{
                            width: "0.75rem",
                            height: "0.75rem",
                            cursor: "pointer",
                            color: colour.primary,
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Filtered Options List */}
                    <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                      {filteredOptions(activeMainCategory, subCategory).length >
                      0 ? (
                        filteredOptions(activeMainCategory, subCategory).map(
                          (item) => (
                            <Box
                              key={item.id}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                padding: "0.5rem 1.5rem",
                                borderBottom: "1px solid rgba(0,0,0,0.1)",
                                "&:last-child": {
                                  borderBottom: "none",
                                },
                              }}
                            >
                              <Box>{item.product}</Box>
                              <Box>₦{item.price.toLocaleString()}</Box>
                            </Box>
                          )
                        )
                      ) : (
                        <Box
                          sx={{
                            padding: "0.5rem",
                            color: "rgba(0,0,0,0.7)",
                            textAlign: "center",
                          }}
                        >
                          No items found matching "{searchTerms[subCategory]}"
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MenuBar;
